import React, { useState, useEffect } from "react";
import axios from "./utils/axios";
import "./App.css";
import Filters from "./components/Filters";
import OrderCard from "./components/OrderCard";
import OrderDetail from "./components/OrderDetail";
import { agruparEvento } from "./utils/agruparEvento";
import AdminUsuarios from "./components/AdminUsuarios";
import { useAuth } from "./context/AuthProvider";

const PAGE_SIZE = 20;

export default function App() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  const [allOrders, setAllOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Abierto");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [vendedorFilter, setVendedorFilter] = useState("");
  const [vendedores, setVendedores] = useState([]);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cabecera, setCabecera] = useState([]);
  const [detadln, setDetadln] = useState([]);
  const [detapick, setDetapick] = useState([]);
  const [draft, setDraft] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [selectedStep, setSelectedStep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    setLoadingOrders(true);
    axios
      .get("/orders")
      .then((res) => {
        const sorted = [...res.data].sort(
          (a, b) => new Date(b.CreateDate) - new Date(a.CreateDate),
        );
        setAllOrders(sorted);
        setFilteredOrders(sorted);
        loadPage(sorted, 1);
        const vendedoresUnicos = [
          ...new Set(res.data.map((o) => o.Vendedor).filter(Boolean)),
        ];
        setVendedores(vendedoresUnicos.sort());
      })
      .catch(console.error)
      .finally(() => setLoadingOrders(false));
  }, [isAuthenticated]);

  const handleFilters = () => {
    let filtered = [...allOrders];

    if (search) {
      const term = search.toLowerCase();
      filtered = filtered.filter((o) => {
        const values = [
          o.DocNum,
          o.DraftEntry,
          o.Cliente,
          o.NumAtCard,
          o.Razon_Social?.toLowerCase(),
        ];
        return values.some((v) => v?.toString().toLowerCase().includes(term));
      });
    }

    if (statusFilter !== "Todos") {
      filtered = filtered.filter((o) => o.Estatus === statusFilter);
    }

    if (vendedorFilter) {
      filtered = filtered.filter((o) => o.Vendedor === vendedorFilter);
    }

    if (dateFrom && dateTo) {
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      filtered = filtered.filter((o) => {
        const cd = new Date(o.CreateDate);
        return cd >= from && cd <= to;
      });
    }

    setFilteredOrders(filtered);
    setOrders([]);
    loadPage(filtered, 1);
  };

  useEffect(() => {
    handleFilters();
  }, [search, statusFilter, dateFrom, dateTo, vendedorFilter]);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("Abierto");
    setDateFrom("");
    setDateTo("");
    setVendedorFilter("");
  };

  const loadPage = (data, pageNumber) => {
    const start = (pageNumber - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    setOrders(data.slice(0, end));
    setPage(pageNumber);
  };

  const openOrder = async (order) => {
    const draftId = parseInt(order?.DraftEntry);
    if (!draftId || isNaN(draftId)) return;

    setSelectedOrder(order);
    setLoading(true);

    try {
      const [cab, dln, pick, draftResp] = await Promise.all([
        axios.get(`/pedidos/${draftId}/cabecera`),
        axios.get(`/pedidos/${draftId}/detadln`),
        axios.get(`/pedidos/${draftId}/detapick`),
        axios.get(`/pedidosdrafts/${draftId}`),
      ]);

      setCabecera(cab.data);
      setDetadln(dln.data);
      setDetapick(pick.data);
      setDraft(draftResp.data?.[0] ?? null);

      const eventos = [];
      eventos.push(
        ...agruparEvento(cab.data, "📥 Pedido", "TaxDate", "DocNum"),
      );
      eventos.push(
        ...agruparEvento(pick.data, "📦 Picking", "TaxDate", "AbsEntry", false),
      );

      const remitos = agruparEvento(
        dln.data,
        "🚛 Remito",
        "Date_Entrega_SAP",
        "Folio",
      );
      remitos.forEach((e) => {
        const entregaReal = e.data.find(
          (d) => d.Entrega_Real_SAP,
        )?.Entrega_Real_SAP;
        if (entregaReal) {
          e.entrega_real = entregaReal;
        }
      });
      eventos.push(...remitos);

      const parseFechaTS = (fecha, ts) => {
        if (!fecha) return new Date(0);
        const hora = ts?.toString().padStart(6, "0") || "000000";
        const hh = hora.slice(0, 2);
        const mm = hora.slice(2, 4);
        const ss = hora.slice(4, 6);
        return new Date(`${fecha}T${hh}:${mm}:${ss}`);
      };

      const eventosOrdenados = eventos
        .map((e) => ({ ...e, _orden: parseFechaTS(e.fecha, e.ts) }))
        .sort((a, b) => a._orden - b._orden);

      setTimeline(eventosOrdenados);
    } catch (e) {
      console.error("❌ Error al cargar datos", e);
    } finally {
      setLoading(false);
    }
  };

  const closeOrder = () => {
    setSelectedOrder(null);
    setSelectedStep(null);
    setTimeline([]);
    setDraft(null);
    setCabecera([]);
    setDetadln([]);
    setDetapick([]);
  };

  if (!isAuthenticated) {
    return <div>🔐 Cargando autenticación...</div>;
  }

  return (
    <div
      style={{
        padding: 32,
        fontFamily: "Arial",
        background: "#F7FAFC",
        minHeight: "100vh",
      }}
    >
      <h1 style={{ fontSize: 32, fontWeight: 600, color: "#2D3748" }}>
        Order Tracking
      </h1>

      <p style={{ marginBottom: 10 }}>
        Bienvenido, <strong>{user?.name}</strong> ({user?.email}){" "}
        <button onClick={logout} style={{ marginLeft: 10 }}>
          🚪 Cerrar sesión
        </button>
      </p>

      {isAdmin && (
        <button
          onClick={() => setShowAdminPanel((prev) => !prev)}
          style={{
            marginBottom: 20,
            padding: "10px 20px",
            background: "#4A5568",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {showAdminPanel
            ? "🔙 Volver a pedidos"
            : "⚙️ Administración de usuarios"}
        </button>
      )}

      {showAdminPanel ? (
        <AdminUsuarios />
      ) : !selectedOrder ? (
        <>
          <Filters
            search={search}
            setSearch={setSearch}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            vendedorFilter={vendedorFilter}
            setVendedorFilter={setVendedorFilter}
            vendedores={vendedores}
            resetFilters={resetFilters}
          />

          {loadingOrders ? (
            <p style={{ fontSize: 16, color: "#4A5568", marginTop: 40 }}>
              🔄 Cargando pedidos...
            </p>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {orders.map((order) => (
                <OrderCard
                  key={order.DraftEntry}
                  order={order}
                  onClick={() => openOrder(order)}
                />
              ))}
            </div>
          )}

          {!loadingOrders && orders.length < filteredOrders.length && (
            <button
              onClick={() => loadPage(filteredOrders, page + 1)}
              style={{
                marginTop: 20,
                padding: "10px 20px",
                borderRadius: 8,
                border: "none",
                background: "#2D3748",
                color: "white",
                cursor: "pointer",
              }}
            >
              Cargar más
            </button>
          )}
        </>
      ) : (
        <OrderDetail
          selectedOrder={selectedOrder}
          draft={draft}
          timeline={timeline}
          loading={loading}
          selectedStep={selectedStep}
          setSelectedStep={setSelectedStep}
          closeOrder={closeOrder}
        />
      )}
    </div>
  );
}
